// Generic no-silent-top-N helper: constant head, one tap reveals ALL
// remaining items, "Show fewer" collapses back, and the count line is ALWAYS
// visible. The overflow block animates with the same height/opacity idiom as
// the NotesBody reveal (DURATION.medium/EASE.out in, DURATION.short/EASE.in
// out, reduced-motion honored).
//
// API: a render-prop over an index range, called twice — once for the
// always-visible head slice, once (inside the animated block) for the
// overflow slice — so grid-based tables keep their column alignment.

import { useId, useState, type ReactNode } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { DURATION, EASE } from "./motion";

export interface SeeMoreRange {
  start: number;
  end: number;
}

interface SeeMoreSectionProps {
  /** Constant head count — never changes with filters. */
  head: number;
  /** Size of the complete underlying set (e.g. 105 stores). */
  total: number;
  /** Size of the filtered/matching set; defaults to `total`. */
  matching?: number;
  /** Plural noun for the count line ("stores", "rows", "programs"). */
  noun: string;
  /** Renders items[start..end) — called for the head slice and the overflow slice. */
  children: (range: SeeMoreRange) => ReactNode;
}

export function SeeMoreSection({
  head,
  total,
  matching,
  noun,
  children,
}: SeeMoreSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();
  const prefersReduced = useReducedMotion();

  const matchCount = matching ?? total;
  const headCount = Math.min(head, matchCount);
  const overflowCount = matchCount - headCount;
  const shownCount = expanded ? matchCount : headCount;

  const countLine =
    matchCount !== total
      ? `Showing ${shownCount} of ${matchCount} matching · ${total} total ${noun}`
      : `Showing ${shownCount} of ${total} ${noun}`;

  return (
    <div>
      {children({ start: 0, end: headCount })}
      <AnimatePresence initial={false}>
        {expanded && overflowCount > 0 ? (
          <motion.div
            key="overflow"
            id={regionId}
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
              transition: prefersReduced
                ? { duration: 0 }
                : { duration: DURATION.medium, ease: EASE.out },
            }}
            exit={{
              height: 0,
              opacity: 0,
              transition: prefersReduced
                ? { duration: 0 }
                : { duration: DURATION.short, ease: EASE.in },
            }}
            style={{ overflow: "hidden" }}
          >
            {children({ start: headCount, end: matchCount })}
          </motion.div>
        ) : null}
      </AnimatePresence>
      <div className="flex items-center justify-between gap-3 px-5 py-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground tabular-nums">
          {countLine}
        </span>
        {overflowCount > 0 || expanded ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-controls={regionId}
            className="gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            {expanded ? (
              <>
                Show fewer
                <ChevronUp className="h-3.5 w-3.5" />
              </>
            ) : (
              <>
                See all {matchCount.toLocaleString()}
                <ChevronDown className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
